import {
  AttributeTableItem,
  ClassTableItem,
  RelationshipTableItem,
  TableField,
} from 'src/containers/ProjectDiagram/components/modelList/types';
import { MappingField } from 'src/containers/ProjectDiagram/types/data';
import { MappingSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { IgnoredSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getOrganizedSuggestionRecords';

export enum ModelListTab {
  CLASSES = 'CLASSES',
  RELATIONSHIPS = 'RELATIONSHIPS',
  ATTRIBUTES = 'ATTRIBUTES',
}

export type ModelListTableData<TableItem> = {
  fields: Array<
    TableField<TableItem> & {
      fieldIdx: number;
      hidden?: boolean;
    }
  >;
  filter: string;
  sort: { direction: 'asc' | 'desc'; field: string } | null;
  widths: number[] | undefined;
};

export enum EntityDrawerType {
  NONE = 'NONE',
  CLASS = 'CLASS',
  RELATIONSHIP = 'RELATIONSHIP',
  ATTRIBUTE = 'ATTRIBUTE',
  RESOURCE = 'RESOURCE',
  SENSITIVE_PROPERTY_GROUP = 'SENSITIVE_PROPERTY_GROUP',
}

export enum ProjectSortOption {
  NAME = 'Name',
  CREATED_AT = 'Created',
  UPDATED_AT = 'Updated',
}

type EntityDrawerExtraOptions = {
  isEditingAttributes?: boolean;
};

export type EntityDrawer = {
  entityValue?: string;
  type: EntityDrawerType;
} & EntityDrawerExtraOptions;

export type UiState = {
  filtersByProjectMapping: Record<
    string, // project id
    Record<
      string, // mapping id
      Record<
        string, // property iri
        boolean
      >
    >
  >;
  isBrowserAuthFailedDialogOpen: boolean;
  isCreateProjectOverlayOpen: boolean;
  isSavingProject: boolean;
  isProjectModelDialogOpen: boolean;
  isProjectModelDialogPendingOpen: boolean;
  isProjectResourceDialogOpen: boolean;
  isProjectResourceDialogPendingOpen: boolean;
  isProjectSettingsDrawerOpen: boolean;
  isPublishProjectDrawerOpen: boolean;
  isSignInDialogOpen: boolean;
  isVoiceboxDrawerOpen: boolean;
  isVoiceboxDrawerPendingOpen: boolean;
  isVoiceboxDrawerSummaryOpen: boolean;
  isVoiceboxDrawerSummaryPendingOpen: boolean;
  createClassDrawer: {
    isOpen: boolean;
    initialValue?: string;
  };
  createRelationshipDrawer: {
    isOpen: boolean;
    sourceClassIri?: string;
    targetClassIri?: string;
  };
  createSensitivePropertyGroupDrawer: {
    isOpen: boolean;
    attributeIri?: string;
  };
  createMappingDialog: {
    initialValue?: string;
    isOpen: boolean;
    fromId?: string;
    fields?: Record<string, MappingField>;
    toValue?: string;
  };
  entityDrawer: EntityDrawer & {
    history: EntityDrawer[];
    projectId: string | null;
  };
  modelList: {
    isOpen: boolean;
    projectId: string | null;
    tabId: ModelListTab;
    tableData: {
      [ModelListTab.CLASSES]: ModelListTableData<ClassTableItem> | null;
      [ModelListTab.RELATIONSHIPS]: ModelListTableData<RelationshipTableItem> | null;
      [ModelListTab.ATTRIBUTES]: ModelListTableData<AttributeTableItem> | null;
    };
  };
  projectMappingSettingsDrawer: {
    isOpen: boolean;
    mappingId?: string;
  };
  projectSortOption: ProjectSortOption;
  renameProjectModelDialog: {
    isOpen: boolean;
    modelId?: string;
  };
  ruleDetailDrawer: {
    isOpen: boolean;
    ruleId?: string;
  };
  ruleOverviewDrawer: {
    isOpen: boolean;
  };
  fileUploadPreviewDialog: {
    isOpen: boolean;
    resourceId?: string;
  };
  resourceSuggestionsDialog: {
    initialClassIris?: string[];
    initialIgnoredSuggestions?: IgnoredSuggestion[];
    initialMappingSuggestions?: MappingSuggestion[];
    initialResourceIds?: string[];
    isOpen: boolean;
  };
  virtualGraphPreviewDialog: {
    isOpen: boolean;
    resourceId?: string;
  };
};

export const createInitialUiState = (): UiState => ({
  filtersByProjectMapping: {},
  isBrowserAuthFailedDialogOpen: false,
  isCreateProjectOverlayOpen: false,
  isSavingProject: false,
  isProjectModelDialogOpen: false,
  isProjectModelDialogPendingOpen: false,
  isProjectResourceDialogOpen: false,
  isProjectResourceDialogPendingOpen: false,
  isProjectSettingsDrawerOpen: false,
  isPublishProjectDrawerOpen: false,
  isSignInDialogOpen: false,
  isVoiceboxDrawerOpen: false,
  isVoiceboxDrawerPendingOpen: false,
  isVoiceboxDrawerSummaryOpen: false,
  isVoiceboxDrawerSummaryPendingOpen: false,
  createClassDrawer: {
    isOpen: false,
  },
  createRelationshipDrawer: {
    isOpen: false,
  },
  createSensitivePropertyGroupDrawer: {
    isOpen: false,
  },
  createMappingDialog: {
    isOpen: false,
  },
  entityDrawer: {
    history: [],
    projectId: null,
    type: EntityDrawerType.NONE,
  },
  modelList: {
    isOpen: false,
    projectId: null,
    tabId: ModelListTab.CLASSES,
    tableData: {
      [ModelListTab.CLASSES]: null,
      [ModelListTab.RELATIONSHIPS]: null,
      [ModelListTab.ATTRIBUTES]: null,
    },
  },
  projectMappingSettingsDrawer: {
    isOpen: false,
  },
  projectSortOption: ProjectSortOption.UPDATED_AT,
  renameProjectModelDialog: {
    isOpen: false,
  },
  ruleDetailDrawer: {
    isOpen: false,
  },
  ruleOverviewDrawer: {
    isOpen: false,
  },
  fileUploadPreviewDialog: {
    isOpen: false,
  },
  resourceSuggestionsDialog: {
    isOpen: false,
  },
  virtualGraphPreviewDialog: {
    isOpen: false,
  },
});
